/**
 * 🔐 Admin Login Gate - Protege rutas de Super Admin
 *
 * Solo para super admin. Detecta si el usuario está en admin.smarty.lat y verifica:
 * 1. Si está logueado
 * 2. Si tiene rol de Super Admin
 * 3. Si no, redirige al login
 *
 * Además ARMA EL SHELL del super admin: el mismo `<Frame>` del producto con
 * `navigation` (sidebar izquierdo, `SuperAdminNavigation`) + `topBar` (logout) +
 * `AdminToastProvider` (para useAdminToast). La sección se resuelve por la URL en
 * `SuperAdminPage`.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, TopBar, Box, BlockStack, Spinner, Text } from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';
import LoginPage from '@/pages/LoginPage';
import { getBaseUrl } from '@/lib/url-utils';
import { getWebHost } from '@/config/app-domains.config';
import { AdminToastProvider } from '@/contexts/AdminToastContext';
import { useSpaInternalNavCapture } from '@/hooks/useSpaInternalNavCapture';
import { SuperAdminNavigation } from './SuperAdminNavigation';

interface AdminLoginGateProps {
  children: React.ReactNode;
}

export function AdminLoginGate({ children }: AdminLoginGateProps) {
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  // Captura clics de <a> internos (incl. los del sidebar Polaris) → navegación SPA
  // sin recargar la página (evita re-verificar el gate en cada cambio de sección).
  useSpaInternalNavCapture(navigate);

  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          // No está logueado, mostrar login
          setIsSuperAdmin(false);
          setLoading(false);
          return;
        }

        // Una sola pregunta, a la base: ¿este usuario es super admin?
        //
        // Antes había TRES caminos alternativos unidos por OR, y el tercero
        // decía `if (staffProfile || ...)`: bastaba con TENER un perfil de staff
        // —cualquier empleado de cualquier tenant— para entrar. Estaba anotado
        // como `TODO: Agregar campo is_super_admin en staff_profiles`, o sea que
        // el hueco se conocía y quedó abierto.
        //
        // Los otros dos también sobraban: `user_metadata` lo puede escribir el
        // propio usuario en algunos flujos, y la lista de emails viajaba EN EL
        // BUNDLE (publicaba el correo del super admin y se desincronizaba de la
        // copia de la base y de la de la edge function).
        //
        // `is_super_admin()` —la sobrecarga SIN argumento— lee `platform_admins`,
        // la única fuente de verdad. Sin argumento a propósito: la variante
        // `is_super_admin(text)` sí permite preguntar por otro y por eso ya no se
        // le concede a `anon`. Este gate es la UI; lo que protege los datos son
        // las policies RLS, que leen exactamente la misma tabla.
        const { data: esAdmin, error } = await supabase.rpc('is_super_admin');
        if (error) {
          // Falla CERRADO. Ante la duda, no se entra.
          devLog.error('No se pudo verificar super admin:', error);
          setIsSuperAdmin(false);
          setLoading(false);
          return;
        }

        if (esAdmin === true) {
          setIsSuperAdmin(true);
        } else {
          setIsSuperAdmin(false);
          // Está logueado pero no es super admin: al panel de mercaderes.
          window.location.href = getBaseUrl(getWebHost());
        }
        setLoading(false);
      } catch (error) {
        devLog.error('Error verificando super admin:', error);
        setIsSuperAdmin(false);
        setLoading(false);
      }
    };

    checkSuperAdmin();

    // Escuchar cambios de sesión
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setIsSuperAdmin(false);
      } else if (event === 'SIGNED_IN' && session) {
        checkSuperAdmin();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isSuperAdmin === true) {
      document.body.classList.add('polaris-admin');
      return () => document.body.classList.remove('polaris-admin');
    }
  }, [isSuperAdmin]);

  if (loading) {
    return (
      <Frame>
        <Box
          padding="1600"
          minHeight="100vh"
          as="div"
          background="bg-surface"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <BlockStack gap="300" inlineAlign="center">
            <Spinner accessibilityLabel="Verificando acceso" size="large" />
            <Text as="p" tone="subdued">Verificando acceso...</Text>
          </BlockStack>
        </Box>
      </Frame>
    );
  }

  // Si no es super admin, mostrar login
  if (isSuperAdmin === false) {
    return <LoginPage />;
  }

  const handleLogout = () => {
    void supabase.auth.signOut().finally(() => {
      window.location.href = '/admin/login';
    });
  };

  const topBar = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={() => setMobileNavOpen((o) => !o)}
      userMenu={
        <TopBar.UserMenu
          name="Super Admin"
          initials="SA"
          open={userMenuOpen}
          onToggle={() => setUserMenuOpen((o) => !o)}
          actions={[{ items: [{ content: 'Cerrar sesión', onAction: handleLogout }] }]}
        />
      }
    />
  );

  // Si es super admin: shell del producto (Frame + sidebar + topbar) + AdminToastProvider.
  return (
    <Frame
      navigation={<SuperAdminNavigation />}
      topBar={topBar}
      showMobileNavigation={mobileNavOpen}
      onNavigationDismiss={() => setMobileNavOpen(false)}
    >
      <AdminToastProvider>
        {children}
      </AdminToastProvider>
    </Frame>
  );
}
